import { useEffect, useRef } from "react";

/**
 * Аппаратная кнопка «назад» закрывает оверлей (шторку, диалог, камеру),
 * а не уводит с экрана.
 *
 * Зачем: на телефоне и ТСД «назад» — рефлекс. Без перехвата открытая шторка
 * съедала бы весь экран скана: кладовщик жмёт «назад», чтобы убрать список,
 * и вылетает из сессии.
 *
 * Как: пока оверлей открыт, в историю кладётся служебная запись С ТЕМ ЖЕ URL.
 * «Назад» съедает её, мы ловим `popstate` и закрываем ВЕРХНИЙ оверлей.
 * Закрыли крестиком или тапом по скриму — снимаем запись сами, чтобы история
 * не пухла записями-призраками.
 *
 * Почему это один общий менеджер, а не эффект на каждый оверлей:
 *  - «шторка меню → шторка разбора» — это размонтирование и монтирование в
 *    ОДНОМ коммите React. Наивный вариант успел бы вызвать `history.back()`
 *    (он асинхронный) уже ПОСЛЕ того, как новая шторка положила свою запись, —
 *    и новая шторка захлопнулась бы сама собой. Здесь изменения истории
 *    сводятся в один микротаск, поэтому такая замена не трогает историю вовсе.
 *  - вложенные оверлеи закрываются по одному, сверху вниз.
 *
 * Если сверху лежит ЧУЖАЯ запись (пока оверлей был открыт, произошла настоящая
 * навигация) — историю не трогаем: отменить переход пользователя хуже, чем
 * оставить лишнюю запись.
 */
interface OverlayState {
  __scanOverlay?: number;
}

interface Guard {
  close: () => void;
}

const stack: Guard[] = [];
let entries = 0; // сколько наших записей реально лежит в истории
let selfPops = 0; // popstate, которые вызвали мы сами (это не «назад» пользователя)
let flushQueued = false;
let installed = false;
let seq = 0;

function topIsOurs(): boolean {
  return Boolean((window.history.state as OverlayState | null)?.__scanOverlay);
}

function install(): void {
  if (installed) return;
  installed = true;
  window.addEventListener("popstate", () => {
    if (selfPops > 0) {
      selfPops -= 1;
      return;
    }
    if (entries === 0) return; // не наша запись — обычная навигация роутера
    entries -= 1;
    const guard = stack.pop(); // запись уже съедена: снимать её в cleanup не нужно
    guard?.close();
  });
}

function flush(): void {
  flushQueued = false;
  while (entries < stack.length) {
    const base = (window.history.state ?? {}) as OverlayState;
    // Состояние роутера (usr/key/idx) переносим как есть: react-router читает
    // его на popstate, и потерять эти поля значит сломать ему навигацию.
    window.history.pushState({ ...base, __scanOverlay: ++seq }, "");
    entries += 1;
  }
  while (entries > stack.length) {
    if (!topIsOurs()) {
      entries = 0; // наши записи оказались под чужой навигацией — забываем о них
      break;
    }
    entries -= 1;
    selfPops += 1;
    window.history.back();
  }
}

function schedule(): void {
  if (flushQueued) return;
  flushQueued = true;
  // Микротаск: размонтирование и монтирование в одном коммите React успевают
  // сложиться, и «замена» одной шторки на другую не даёт лишнего back().
  queueMicrotask(flush);
}

export function useBackGuard(active: boolean, onBack: () => void): void {
  const cb = useRef(onBack);
  cb.current = onBack;

  useEffect(() => {
    if (!active) return;
    install();
    const guard: Guard = { close: () => cb.current() };
    stack.push(guard);
    schedule();

    return () => {
      const at = stack.indexOf(guard);
      if (at === -1) return; // уже снят «назадом» — история в порядке
      stack.splice(at, 1);
      schedule();
    };
  }, [active]);
}
